import { ApiError } from "../types/errors/api-error";

/**
 * Client HTTP commun à tous les services KMS.
 *
 * Les appels visent le proxy same-origin `/backend-api`, qui injecte l'en-tête
 * Authorization côté serveur à partir du cookie HttpOnly `keycloak-token` :
 * aucun token n'est manipulé ici.
 *
 * Toutes les réponses non-2xx sont converties en {@link ApiError} ; les
 * services n'ont donc plus à tester `resultat.ok` ni à re-throw.
 */

type RequestOptions = {
  signal?: AbortSignal;
  /** Paramètres de query string. Les valeurs `undefined` sont ignorées. */
  query?: Record<string, string | number | boolean | undefined>;
};

type MutationBody = FormData | Record<string, unknown> | unknown[] | undefined;

export class HttpClient {
  constructor(private readonly apiUrl: string) {}

  /** GET renvoyant du JSON. */
  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.request("GET", path, undefined, options);

    return parseJson<T>(response);
  }

  /** GET renvoyant un fichier (document, licence, ...). */
  async getBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const response = await this.request("GET", path, undefined, options);

    return response.blob();
  }

  /**
   * POST. Le corps est envoyé en JSON, sauf s'il s'agit d'un `FormData` —
   * auquel cas le navigateur pose lui-même le Content-Type et son boundary.
   */
  async post<T = void>(
    path: string,
    body?: MutationBody,
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.request("POST", path, body, options);

    return parseJson<T>(response);
  }

  async put<T = void>(
    path: string,
    body?: MutationBody,
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.request("PUT", path, body, options);

    return parseJson<T>(response);
  }

  async patch<T = void>(
    path: string,
    body?: MutationBody,
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.request("PATCH", path, body, options);

    return parseJson<T>(response);
  }

  async delete<T = void>(
    path: string,
    body?: MutationBody,
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.request("DELETE", path, body, options);

    return parseJson<T>(response);
  }

  private async request(
    method: string,
    path: string,
    body: MutationBody,
    { signal, query }: RequestOptions,
  ): Promise<Response> {
    const isFormData = body instanceof FormData;

    const response = await fetch(`${this.apiUrl}${path}${buildQuery(query)}`, {
      method,
      cache: "no-store",
      signal,
      // Content-Type uniquement pour un corps JSON : un GET/DELETE sans corps
      // n'en a pas besoin, et un FormData doit garder celui du navigateur.
      headers:
        body !== undefined && !isFormData
          ? { "Content-Type": "application/json" }
          : {},
      body: isFormData
        ? body
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
    });

    if (!response.ok) {
      throw await ApiError.fromResponse(response);
    }

    return response;
  }
}

const buildQuery = (query: RequestOptions["query"]): string => {
  if (!query) return "";

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.append(key, String(value));
  }

  const queryString = params.toString();

  return queryString ? `?${queryString}` : "";
};

/**
 * Les endpoints de mutation répondent tantôt avec une représentation, tantôt
 * avec un corps vide (204). On lit donc le texte avant de parser, `res.json()`
 * échouant sur un corps vide.
 */
const parseJson = async <T>(response: Response): Promise<T> => {
  const text = await response.text();

  return (text ? JSON.parse(text) : undefined) as T;
};
